// File: frontend/src/utils/bootstrap.js

import { v4 as uuidv4 } from 'uuid';
import { API_CONFIG } from '../config/api';
import { STORAGE_KEYS } from '../config/constants';

let isBootstrapping = false;
let bootstrapPromise = null;

const isPreRendering = () => {
  return (
    typeof navigator !== 'undefined' &&
    navigator.userAgent === 'ReactSnap'
  );
};

export const getDeviceInfo = () => {
  return {
    user_agent: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    device_type: /mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
  };
};

export const getTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return 'UTC';
  }
};

export const getUTMParameters = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || 'direct',
    utm_medium: urlParams.get('utm_medium') || null,
    utm_campaign: urlParams.get('utm_campaign') || null,
    utm_term: urlParams.get('utm_term') || null,
    utm_content: urlParams.get('utm_content') || null,
    referrer_url: document.referrer || null
  };
};

export const generateSessionIds = () => {
  const userId = uuidv4();
  
  return {
    userId: userId,
    anonymousUserId: userId,
    sessionId: uuidv4()
  };
};

export const getStoredIds = () => {
  return {
    userId: localStorage.getItem(STORAGE_KEYS.USER_ID),
    anonymousUserId: localStorage.getItem(STORAGE_KEYS.ANONYMOUS_USER_ID),
    sessionId: localStorage.getItem(STORAGE_KEYS.SESSION_ID)
  };
};

export const storeSessionIds = (ids) => {
  localStorage.setItem(STORAGE_KEYS.USER_ID, ids.userId);
  localStorage.setItem(STORAGE_KEYS.ANONYMOUS_USER_ID, ids.anonymousUserId);
  localStorage.setItem(STORAGE_KEYS.SESSION_ID, ids.sessionId);
  localStorage.setItem(STORAGE_KEYS.BOOTSTRAP_TIMESTAMP, new Date().toISOString());
};

export const syncAuthenticatedUserIds = (authUser) => {
  if (!authUser || !authUser.user_id) {
    console.error('Cannot sync IDs: invalid auth user data');
    return false;
  }

  localStorage.setItem(STORAGE_KEYS.USER_ID, authUser.user_id);
  localStorage.setItem(STORAGE_KEYS.ANONYMOUS_USER_ID, authUser.user_id);
  
  const existingSessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (!existingSessionId) {
    localStorage.setItem(STORAGE_KEYS.SESSION_ID, uuidv4());
  }
  
  localStorage.setItem(STORAGE_KEYS.BOOTSTRAP_TIMESTAMP, new Date().toISOString());

  return true;
};

const validateStoredIds = (ids) => {
  if (!ids.userId || !ids.sessionId) {
    return false;
  }
  
  if (ids.anonymousUserId && ids.userId !== ids.anonymousUserId) {
    return false;
  }
  
  return true;
};

export const bootstrapAnonymousUser = async () => {
  // Skip entirely during pre-rendering — no API calls, no localStorage writes
  if (isPreRendering()) {
    return { success: true, cached: true, ids: {}, preRendered: true };
  }

  if (isBootstrapping && bootstrapPromise) {
    return bootstrapPromise;
  }

  const existingIds = getStoredIds();
  if (validateStoredIds(existingIds)) {
    return { 
      success: true, 
      cached: true, 
      ids: existingIds 
    };
  }

  isBootstrapping = true;
  
  bootstrapPromise = (async () => {
    try {
      const ids = generateSessionIds();
      const deviceInfo = getDeviceInfo();
      const timezone = getTimezone();
      const utmParams = getUTMParameters();
      
      const bootstrapData = {
        command: 'bootstrapAnonymousUser',
        data: {
          user_id: ids.userId,
          session_id: ids.sessionId,
          enterprise_id: API_CONFIG.enterpriseId,
          primary_language: navigator.language.toLowerCase() || 'en-us',
          entry_page: window.location.href,
          timezone: timezone,
          device_info: deviceInfo,
          ...utmParams
        }
      };

      const response = await fetch(`${API_CONFIG.baseUrl}/frontend-actions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bootstrapData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        storeSessionIds(ids);
        return { success: true, ...result, ids };
      } else {
        throw new Error(result.error || 'Bootstrap failed');
      }

    } catch (error) {
      console.error('Bootstrap error:', error);
      
      const ids = generateSessionIds();
      storeSessionIds(ids);
      
      return { 
        success: false, 
        error: error.message,
        ids,
        fallback: true 
      };
    } finally {
      isBootstrapping = false;
      bootstrapPromise = null;
    }
  })();

  return bootstrapPromise;
};

export const getCurrentUserContext = () => {
  if (isPreRendering()) return null;

  const ids = getStoredIds();
  
  if (!validateStoredIds(ids)) {
    return null;
  }
  
  return {
    userId: ids.userId,
    anonymousUserId: ids.anonymousUserId,
    sessionId: ids.sessionId,
    enterpriseId: API_CONFIG.enterpriseId
  };
};

export const ensureBootstrapped = async () => {
  if (isPreRendering()) {
    return { success: true, ids: {}, preRendered: true };
  }

  const existingIds = getStoredIds();
  
  if (validateStoredIds(existingIds)) {
    return { success: true, ids: existingIds };
  }
  
  return await bootstrapAnonymousUser();
};

export const initializeBootstrap = () => {
  if (typeof window === 'undefined' || isPreRendering()) {
    return;
  }

  bootstrapAnonymousUser();
};

const bootstrap = {
  bootstrapAnonymousUser,
  initializeBootstrap,
  getCurrentUserContext,
  ensureBootstrapped,
  getStoredIds,
  generateSessionIds,
  storeSessionIds,
  syncAuthenticatedUserIds
};
export default bootstrap;